import { apiClient } from "@/services/api.client"
import { loadMe, useMeStore, type Me } from "@/features/auth/hooks/useCurrentUser"

const CHECKOUT_SRC = "https://checkout.razorpay.com/v1/checkout.js"
const POLL_MS = 2000
const POLL_MAX_MS = 30_000

export type PlanChoice = "plus" | "pro"

export interface SubscriptionStart {
  subscription_id: string
  key_id: string
  status: string
  plan?: string
}

/** Plus to Pro: the credit difference as a one-time order (POST /billing/subscription with upgrade: true). */
export interface UpgradeStart {
  upgrade: true
  order_id: string
  amount_minor: number
  currency: string
  key_id: string
  plan: "pro"
}

/** An add-on order (POST /billing/addons/{sku}/order). */
export interface OrderStart {
  order_id: string
  key_id: string
  amount_minor: number
  currency: string
  sku?: string
  quantity?: number
  scope_key?: string | null
}

export interface UpgradeVerified {
  status: "upgraded" | "duplicate" | "pending" | "needs_support"
  kind?: "upgrade"
  subscription_id?: string
  key_id?: string
  pro_starts_billing_at?: string | null
}

export interface SubscriptionState {
  status: string
  subscribed: boolean
  subscription_id: string | null
  current_start: string | null
  current_end: string | null
  cancel_at_cycle_end: boolean
}

export interface RazorpaySuccess {
  razorpay_payment_id: string
  razorpay_subscription_id?: string
  razorpay_order_id?: string
  razorpay_signature: string
}

interface RazorpayOptions {
  key: string
  subscription_id?: string
  order_id?: string
  amount?: number
  currency?: string
  name: string
  description: string
  prefill?: { name?: string; email?: string }
  theme?: { color: string }
  handler: (r: RazorpaySuccess) => void
  modal?: { ondismiss?: () => void }
}

interface RazorpayInstance {
  open: () => void
  on: (event: string, cb: (r: { error?: { description?: string } }) => void) => void
}

declare global {
  interface Window {
    Razorpay?: new (options: RazorpayOptions) => RazorpayInstance
  }
}

export type CheckoutResult = "active" | "dismissed" | "pending" | "unconfirmed"

let scriptLoad: Promise<boolean> | null = null

/** Load checkout.js once; a failed load clears the memo so a retry can try again. */
export function loadCheckoutScript(): Promise<boolean> {
  if (window.Razorpay) return Promise.resolve(true)
  scriptLoad ??= new Promise<boolean>((resolve) => {
    const s = document.createElement("script")
    s.src = CHECKOUT_SRC
    s.async = true
    s.onload = () => resolve(true)
    s.onerror = () => { s.remove(); resolve(false) }
    document.body.appendChild(s)
  }).then((ok) => { if (!ok) scriptLoad = null; return ok })
  return scriptLoad
}

export const subscriptionApi = {
  create: (plan: PlanChoice = "pro") =>
    apiClient.post<SubscriptionStart | UpgradeStart>("/api/v1/billing/subscription", { plan }),
  state: () => apiClient.get<SubscriptionState>("/api/v1/billing/subscription"),
  verify: (r: RazorpaySuccess) =>
    apiClient.post<{ status: string }>("/api/v1/billing/subscription/verify", r),
  verifyUpgrade: (r: RazorpaySuccess) =>
    apiClient.post<UpgradeVerified>("/api/v1/billing/subscription/upgrade/verify", {
      razorpay_order_id: r.razorpay_order_id, razorpay_payment_id: r.razorpay_payment_id, razorpay_signature: r.razorpay_signature,
    }),
  cancel: () =>
    apiClient.post<{ status: string; current_end: string | null; cancel_at_cycle_end: boolean }>(
      "/api/v1/billing/subscription/cancel",
    ),
}

export const addonApi = {
  order: (sku: string, scopeKey?: string | null) =>
    apiClient.post<OrderStart>(`/api/v1/billing/addons/${encodeURIComponent(sku)}/order`, scopeKey ? { scope_key: scopeKey } : {}),
  verify: (r: RazorpaySuccess) =>
    apiClient.post<{ status: "granted" | "duplicate" | "pending"; sku?: string }>("/api/v1/billing/addons/verify", {
      razorpay_order_id: r.razorpay_order_id, razorpay_payment_id: r.razorpay_payment_id, razorpay_signature: r.razorpay_signature,
    }),
}

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms))

export const UNCONFIRMED_COPY = "We couldn't confirm yet \u2014 refresh in a minute."

/**
 * Poll /me until `done` holds (by default: it reports a subscription; the webhook may land after verify).
 * Gives up after 30 s (false) and stops at once when /me itself fails ("error"), so the caller can say so.
 */
export async function pollUntilSubscribed(
  maxMs = POLL_MAX_MS,
  stepMs = POLL_MS,
  done: (e: NonNullable<Me["entitlements"]>) => boolean = (e) => e.subscribed,
): Promise<boolean | "error"> {
  const deadline = Date.now() + maxMs
  for (;;) {
    await loadMe(true)
    if (useMeStore.getState().error) return "error"
    const e = useMeStore.getState().me?.entitlements
    if (e && done(e)) return true
    if (Date.now() >= deadline) return false
    await wait(stepMs)
  }
}

/** Open Razorpay checkout and settle with the signed result, or null when it is closed. A failed attempt keeps it open for a retry. */
async function openCheckout(
  options: Omit<RazorpayOptions, "name" | "description" | "prefill" | "theme" | "handler" | "modal">,
  description: string,
  prefill: { name?: string; email?: string } | undefined,
  onPaymentFailed?: (message: string) => void,
): Promise<RazorpaySuccess | null> {
  if (!(await loadCheckoutScript()) || !window.Razorpay) {
    throw new Error("Razorpay checkout could not be loaded. Check your connection and try again.")
  }
  const Razorpay = window.Razorpay
  return new Promise<RazorpaySuccess | null>((resolve) => {
    const rzp = new Razorpay({
      ...options,
      name: "Nira",
      description,
      prefill,
      theme: { color: "#1e293b" },
      handler: (r) => resolve(r),
      modal: { ondismiss: () => resolve(null) },
    })
    rzp.on("payment.failed", (r) => onPaymentFailed?.(r.error?.description || "Payment failed. Try again."))
    rzp.open()
  })
}

const isUpgrade = (r: SubscriptionStart | UpgradeStart): r is UpgradeStart => "upgrade" in r && r.upgrade === true

/** The sentence for a refusal from the billing endpoints (by its `code`), or the error as it came. */
const REFUSALS: Record<string, string> = {
  already_subscribed: "You already have this plan.",
  downgrade_via_cancel: "Cancel Pro, then choose Plus after your paid period.",
  unknown_plan: "That plan is not available.",
  pro_required: "Add-ons are part of Nira Pro.",
  scope_key_required: "Open the draft you want to add edits to, then try again.",
  unknown_sku: "That add-on is not available.",
}
function refusal(e: unknown): Error {
  const code = (e as { code?: unknown } | null)?.code
  const text = typeof code === "string" ? REFUSALS[code] : undefined
  return text ? new Error(text) : e instanceof Error ? e : new Error(String(e))
}

/** Pro is live when /me says so (a Plus reader is already "subscribed" before that). */
const proLive = (e: NonNullable<Me["entitlements"]>) => e.subscribed && (!e.plan_state || e.plan_state === "pro")

/**
 * Subscription checkout for a plan: create the subscription, open Razorpay checkout with the server's
 * subscription_id and key_id, verify the signed result, then poll /me until the plan is live.
 * Plus to Pro comes back as an order for the credit difference: order checkout, verify at
 * /subscription/upgrade/verify (Pro is active at once), then subscription checkout so the customer
 * authorises the Pro mandate, then poll /me for plan_state pro.
 */
export async function startSubscription(
  plan: PlanChoice = "pro",
  prefill?: { name?: string; email?: string },
  onPaymentFailed?: (message: string) => void,
): Promise<CheckoutResult> {
  let start: SubscriptionStart | UpgradeStart
  try {
    start = await subscriptionApi.create(plan)
  } catch (e) {
    throw refusal(e)
  }
  if (isUpgrade(start)) {
    const paid = await openCheckout({ key: start.key_id, order_id: start.order_id, amount: start.amount_minor, currency: start.currency }, "Nira Pro, Plus credited", prefill, onPaymentFailed)
    if (!paid) return "dismissed"
    const v = await subscriptionApi.verifyUpgrade(paid)
    if (v.status === "needs_support") throw new Error("Contact support: your payment was received but the upgrade did not finish.")
    if (v.status === "upgraded" && v.subscription_id && v.key_id) {
      // Pro is active already; this authorises the monthly mandate. Closing it leaves Pro as it is.
      await openCheckout({ key: v.key_id, subscription_id: v.subscription_id }, "Nira Pro, monthly", prefill, onPaymentFailed)
    }
    const polled = await pollUntilSubscribed(POLL_MAX_MS, POLL_MS, proLive)
    return polled === "error" ? "unconfirmed" : polled ? "active" : "pending"
  }
  const paid = await openCheckout({ key: start.key_id, subscription_id: start.subscription_id }, plan === "plus" ? "Nira Plus, monthly" : "Nira Pro, monthly", prefill, onPaymentFailed)
  if (!paid) return "dismissed"
  await subscriptionApi.verify(paid)
  const polled = await pollUntilSubscribed(POLL_MAX_MS, POLL_MS, plan === "pro" ? proLive : undefined)
  return polled === "error" ? "unconfirmed" : polled ? "active" : "pending"
}

/** Buy an add-on (Pro only): order, checkout, verify (which grants it), then refresh /me. "paid" or "dismissed". */
export async function buyAddon(
  sku: string,
  scopeKey?: string | null,
  prefill?: { name?: string; email?: string },
  onPaymentFailed?: (message: string) => void,
): Promise<"paid" | "dismissed"> {
  let order: OrderStart
  try {
    order = await addonApi.order(sku, scopeKey)
  } catch (e) {
    throw refusal(e)
  }
  const paid = await openCheckout({ key: order.key_id, order_id: order.order_id, amount: order.amount_minor, currency: order.currency }, "Nira Pro add-on", prefill, onPaymentFailed)
  if (!paid) return "dismissed"
  await addonApi.verify(paid)
  await loadMe(true)
  return "paid"
}
